import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';

import {

  Background,

  Controls,

  MiniMap,

  ReactFlow,

  addEdge,

  useEdgesState,

  useNodesState,

  type Connection,

  type Edge,

  type Node,

} from '@xyflow/react';

import '@xyflow/react/dist/style.css';

import { apTheme } from '../../utils/agentPlatformTheme';



type NodeRedNode = {

  id: string;

  type: string;

  z?: string;

  name?: string;

  label?: string;

  disabled?: boolean;

  x?: number;

  y?: number;

  wires?: string[][];

};



type Props = {

  definitionJson: string;

  flowCode: string;

  onDefinitionChange: (json: string) => void;

  className?: string;

};



export type FlowComposerFlowCanvasHandle = {

  addNode: (nodeType: string, label: string) => void;

};



function parseDefinition(json: string, flowCode: string): NodeRedNode[] {

  try {

    const arr = JSON.parse(json) as NodeRedNode[];

    return Array.isArray(arr) ? arr : [];

  } catch {

    return [

      { id: `know-${flowCode}`, type: 'tab', label: flowCode, disabled: false } as NodeRedNode,

    ];

  }

}



function toFlowNodes(items: NodeRedNode[], tabId: string): Node[] {

  return items

    .filter(n => n.type !== 'tab' && (n.z === tabId || !n.z))

    .map(n => ({

      id: n.id,

      type: 'default',

      position: { x: n.x ?? 100, y: n.y ?? 100 },

      data: { label: `${n.name ?? n.type}\n(${n.type})` },

      style: {

        background: '#2a2a3e',

        color: '#e2e8f0',

        border: '1px solid rgba(129, 140, 248, 0.4)',

        borderRadius: '10px',

        fontSize: '12px',

        padding: '8px 12px',

      },

    }));

}



function toFlowEdges(items: NodeRedNode[]): Edge[] {

  const edges: Edge[] = [];

  for (const node of items) {

    if (!node.wires) continue;

    node.wires.forEach((targets, wireIndex) => {

      targets.forEach(targetId => {

        edges.push({

          id: `${node.id}-${wireIndex}-${targetId}`,

          source: node.id,

          target: targetId,

          style: { stroke: '#818cf8', strokeWidth: 2 },

        });

      });

    });

  }

  return edges;

}



function serializeDefinition(

  tab: NodeRedNode,

  nodes: Node[],

  edges: Edge[],

  existing: NodeRedNode[]

): string {

  const others = existing.filter(n => n.type === 'tab' && n.id !== tab.id);

  const flowNodes: NodeRedNode[] = nodes.map(n => {

    const prev = existing.find(e => e.id === n.id);

    const outEdges = edges.filter(e => e.source === n.id);

    const wires: string[][] = outEdges.length > 0 ? [outEdges.map(e => e.target)] : prev?.wires ?? [];

    return {

      id: n.id,

      type: prev?.type ?? 'function',

      z: tab.id,

      name: String(n.data.label ?? n.id).split('\n')[0],

      x: Math.round(n.position.x),

      y: Math.round(n.position.y),

      wires,

    };

  });



  return JSON.stringify([tab, ...others, ...flowNodes], null, 2);

}



export const FlowComposerFlowCanvas = forwardRef<FlowComposerFlowCanvasHandle, Props>(

  function FlowComposerFlowCanvas({ definitionJson, flowCode, onDefinitionChange, className = '' }, ref) {

    const tabId = `know-${flowCode}`;



    const parsed = useMemo(() => parseDefinition(definitionJson, flowCode), [definitionJson, flowCode]);



    const tabNode = useMemo(() => {

      const tab = parsed.find(n => n.type === 'tab');

      return tab ?? { id: tabId, type: 'tab', label: flowCode, disabled: false };

    }, [parsed, tabId, flowCode]);



    const initialNodes = useMemo(() => toFlowNodes(parsed, tabNode.id), [parsed, tabNode.id]);

    const initialEdges = useMemo(() => toFlowEdges(parsed), [parsed]);



    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);

    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);



    useEffect(() => {

      setNodes(toFlowNodes(parsed, tabNode.id));

      setEdges(toFlowEdges(parsed));

    }, [definitionJson, flowCode, parsed, setEdges, setNodes, tabNode.id]);



    const sync = useCallback(

      (nextNodes: Node[], nextEdges: Edge[]) => {

        onDefinitionChange(serializeDefinition(tabNode, nextNodes, nextEdges, parsed));

      },

      [onDefinitionChange, tabNode, parsed]

    );



    const onConnect = useCallback(

      (connection: Connection) => {

        setEdges((eds: Edge[]) => {

          const next = addEdge(connection, eds);

          sync(nodes, next);

          return next;

        });

      },

      [nodes, setEdges, sync]

    );



    const onNodeDragStop = useCallback(() => {

      sync(nodes, edges);

    }, [nodes, edges, sync]);



    const addPaletteNode = useCallback(

      (nodeType: string, label: string) => {

        const id = `${tabNode.id}-${nodeType.replace(/\s/g, '-')}-${Date.now()}`;

        const newNode: Node = {

          id,

          type: 'default',

          position: { x: 120 + nodes.length * 40, y: 120 + nodes.length * 30 },

          data: { label: `${label}\n(${nodeType})` },

          style: {

            background: '#2a2a3e',

            color: '#e2e8f0',

            border: '1px solid rgba(129, 140, 248, 0.4)',

            borderRadius: '10px',

            fontSize: '12px',

            padding: '8px 12px',

          },

        };

        const meta: NodeRedNode = {

          id,

          type: nodeType,

          z: tabNode.id,

          name: label,

          x: newNode.position.x,

          y: newNode.position.y,

          wires: [],

        };

        const nextNodes = [...nodes, newNode];

        setNodes(nextNodes);

        onDefinitionChange(

          serializeDefinition(tabNode, nextNodes, edges, [...parsed, meta])

        );

      },

      [tabNode, nodes, edges, parsed, setNodes, onDefinitionChange]

    );



    useImperativeHandle(ref, () => ({

      addNode: addPaletteNode,

    }));



    return (

      <div className={`flex flex-col min-h-0 overflow-hidden ${apTheme.composerCanvas} ${className}`}>

        <ReactFlow

          nodes={nodes}

          edges={edges}

          onNodesChange={onNodesChange}

          onEdgesChange={onEdgesChange}

          onConnect={onConnect}

          onNodeDragStop={onNodeDragStop}

          fitView

          colorMode="dark"

        >

          <Background color="#ffffff" gap={24} size={1} style={{ opacity: 0.06 }} />

          <Controls className="!bg-[#242120] !border-white/10 !shadow-lg [&>button]:!bg-[#363130] [&>button]:!border-white/10 [&>button]:!text-slate-200 [&>button:hover]:!bg-red-600/30" />

          <MiniMap

            nodeColor="#6366f1"

            maskColor="rgba(0,0,0,0.6)"

            className="!bg-[#161622] !border-white/10"

          />

        </ReactFlow>

      </div>

    );

  }

);


